import { Fragment, useEffect, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { AnimatePresence, m, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import throttle from "lodash-es/throttle"
import { useBoolean } from "react-use"

import { ETypographyColor, ETypographySize, ETypographyWeight, Typography } from "@/3514/components"
import { safeTransition } from "@/3514/utils"
import { seconds } from "@/3514/utils/time.utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import { CoachingChatMatrixFeedbackModalOpener as MatrixFeedbackModalOpener } from "../modals/matrix-feedback"
import {
    useCoachingChatConversationDispatcher as useChatDispatcher,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatConversationSummarySelector as useConversationSummarySelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import { ECoachingChatConversationStatus as EConversationStatus, TCoachingChatSummaryCard } from "../types"

import { CoachingChatClosedIndicator as ClosedIndicator } from "./coaching-chat.closed-indicator.component"
import { CoachingChatSummaryCards as SummaryCards } from "./coaching-chat.summary-cards.component"

const { SUMMARY_GENERATION_DURATION_IN_MS } = uiConfig

const {
    conversation: { summary: classes }
} = chatStyles

const Summary: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const isMotionReduced: boolean = useReducedMotion()

    const {
        entityId,
        conversation: { feedRef, feedScrollerHook }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const scrollFeedToBottom: TEmptyCallback = feedScrollerHook(feedRef)

    const { changeStatus }: ReturnType<typeof useChatDispatcher> = useChatDispatcher(entityId)

    const summary: TCoachingChatSummaryCard[] = useConversationSummarySelector(entityId)

    const status: EConversationStatus = useConversationStatusSelector(entityId)

    const [isGenerationBoxVisible, setIsGenerationBoxVisible]: ReturnType<typeof useBoolean> = useBoolean(false)

    // Tracks the live "generating -> generated" transition only. It must NOT be seeded from the
    // store: on a fresh page load the store is still empty during this first render (the
    // conversation is hydrated later, from an effect), so a seeded ref would latch `false`
    // forever and an already-finished chat would never show its summary again.
    const isGenerationDoneRef: RefObject<boolean> = useRef<boolean>(false)

    // Restored from the server instead: a completed conversation that already carries a summary
    // has nothing left to animate, so it is simply shown.
    const isSummaryRestored: boolean = eq(status, EConversationStatus.Completed) && !isEmpty(summary)

    const isGeneratingSummary: boolean = eq(status, EConversationStatus.GeneratingSummary)

    useEffect((): TEmptyCallback => {
        if (!isGeneratingSummary) return emptyCallback

        safeTransition((): void => setIsGenerationBoxVisible(true))
        const timeout: ReturnType<typeof setTimeout> = setTimeout((): void => {
            safeTransition((): void => {
                setIsGenerationBoxVisible(false)
                isGenerationDoneRef.current = true
            })
        }, SUMMARY_GENERATION_DURATION_IN_MS)

        return (): void => (clearTimeout(timeout), void 0)
    }, [isGeneratingSummary])

    const onAnimationEndThrottled: TEmptyCallback = throttle((): void => {
        changeStatus(EConversationStatus.Completed)
        scrollFeedToBottom()
    }, seconds(0.3))

    return (
        <Fragment>
            <AnimatePresence initial={false} mode="wait">
                {isGenerationBoxVisible && !isEmpty(summary) && (
                    <m.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: isMotionReduced ? 0 : 0.25, ease: [0.4, 0, 0.2, 1] }}
                    >
                        <div className={classes.generatingSummaryBox}>
                            <Typography
                                text={t("entities.coachingChat.chat.summaryGenerationMessage")}
                                size={ETypographySize.Tiny}
                                weight={ETypographyWeight.Bold}
                                color={ETypographyColor.White}
                            />
                        </div>
                    </m.div>
                )}
            </AnimatePresence>

            <AnimatePresence initial={false} mode="wait">
                {!isGenerationBoxVisible && (isGenerationDoneRef.current || isSummaryRestored) && !isEmpty(summary) && (
                    <m.div
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        transition={{ duration: isMotionReduced ? 0 : 0.4, ease: [0.4, 0, 0.2, 1] }}
                        onAnimationComplete={(): void => onAnimationEndThrottled()}
                    >
                        <div className={classes.summaryWrapper}>
                            <SummaryCards cards={summary} />
                            <ClosedIndicator />
                            <MatrixFeedbackModalOpener />
                        </div>
                    </m.div>
                )}
            </AnimatePresence>
        </Fragment>
    )
}

Summary.displayName = "CoachingChatSummary"

export { Summary as CoachingChatSummary }
